import React, { ReactElement, ReactNode } from 'react';
import styled from '@emotion/styled';
import { Check } from '@phosphor-icons/react';
import { Badge, Flex, P, Palette, semantic } from '@updater/ui-uds';
import {
  BadgeConfig,
  CardTarget,
  CardTypes,
  IconName,
} from 'flows/core/types/cards';
import { getIconComponent } from 'utils/get-icon';
import Link from 'next/link';
import { useTracking } from 'react-tracking';
import { TrackEventInput } from '@updater/ui-tracker';
import { CardWrapper } from '../card-wrapper';

type CardPaddingSize = 's' | 'm';
type ResponsiveCardPaddingSize =
  | CardPaddingSize
  | { mobile: CardPaddingSize; desktop: CardPaddingSize };

const resolvePaddingSize = (
  paddingSize: ResponsiveCardPaddingSize = 'm'
): { mobile: CardPaddingSize; desktop: CardPaddingSize } =>
  typeof paddingSize === 'string'
    ? { mobile: paddingSize, desktop: paddingSize }
    : paddingSize;

const CardLink = styled(Link)<{ paddingSize?: ResponsiveCardPaddingSize }>`
  display: block;
  padding: ${({ theme, paddingSize }) =>
    theme.space[resolvePaddingSize(paddingSize).mobile]}px;
  text-decoration: none;
  color: inherit;

  ${({ theme }) => theme.mediaQueries.m} {
    padding: ${({ theme, paddingSize }) =>
      theme.space[resolvePaddingSize(paddingSize).desktop]}px;
  }
`;

const CardContent = styled.div<{ paddingSize?: ResponsiveCardPaddingSize }>`
  padding: ${({ theme, paddingSize }) =>
    theme.space[resolvePaddingSize(paddingSize).mobile]}px;

  ${({ theme }) => theme.mediaQueries.m} {
    padding: ${({ theme, paddingSize }) =>
      theme.space[resolvePaddingSize(paddingSize).desktop]}px;
  }
`;

const ActionHandler: React.FC<
  React.PropsWithChildren<{
    action?: CardTarget;
    actionTracker?: () => void | undefined;
    paddingSize?: ResponsiveCardPaddingSize;
  }>
> = ({ action, actionTracker, paddingSize, children }) => {
  const { trackEvent } = useTracking<TrackEventInput<unknown>>({});
  if (action) {
    const url =
      action.type === 'internal'
        ? { pathname: action.route, query: action.params }
        : action.url;
    return (
      <CardLink
        href={url}
        paddingSize={paddingSize}
        onClick={() =>
          actionTracker ? actionTracker() : trackEvent({ verb: 'clicked' })
        }
      >
        {children}
      </CardLink>
    );
  }
  return <CardContent paddingSize={paddingSize}>{children}</CardContent>;
};

const Card = styled(CardWrapper)`
  border-radius: ${({ theme }) => theme.radii.lg};
  box-shadow: none;
  border: 1px solid ${semantic.border.color.default};
  padding: 0;

  &:hover {
    background-color: ${semantic.surface.hover};
  }
`;

export interface StreamCardProps {
  icon?: ReactElement | IconName;
  heading: string;
  identifier: string;
  description?: string;
  badge?: BadgeConfig;
  badge2?: BadgeConfig;
  action?: CardTarget;
  cardType: CardTypes;
  actionTracker?: () => void | undefined;
  children?: ReactNode;
  paddingSize?: ResponsiveCardPaddingSize;
}

const IconBox = styled(Flex)`
  background-color: ${semantic.surface.selected};
  border: 1px solid ${Palette.blue100};
  border-radius: ${({ theme }) => theme.radii.lg};
  padding: ${({ theme }) => theme.space.xs}px;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const DoneCircle = styled(Flex)`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background-color: ${semantic.action.primary.background};
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const CardTitle = styled.h2`
  margin: 0;
  font-size: ${semantic.font.text.heading.label.size};
  line-height: ${semantic.font.text.heading.label.lineHeight};
  font-weight: ${semantic.font.text.heading.label.weight};
  color: ${semantic.text.color.primary};
`;

const CardTitleDone = styled(CardTitle)`
  text-decoration: line-through;
  color: ${semantic.text.color.disabled};
`;

export const StreamCard: React.FC<StreamCardProps> = ({
  icon,
  heading,
  description,
  badge,
  badge2,
  identifier,
  cardType,
  action,
  actionTracker,
  children,
  paddingSize,
}) => {
  const isDone = badge2?.variant === 'done';
  const iconComponent =
    typeof icon === 'string' ? getIconComponent(icon) : icon;
  const Title = isDone ? CardTitleDone : CardTitle;

  return (
    <Card
      data-testid={`stream-card-${identifier}`}
      data-cy={`stream-card-${identifier}`}
      type={cardType}
    >
      <ActionHandler
        action={action}
        actionTracker={actionTracker}
        paddingSize={paddingSize}
      >
        <Flex alignItems="center" justifyContent="space-between" mb="s">
          <Flex alignItems="center" gap="xs">
            {iconComponent && <IconBox>{iconComponent}</IconBox>}
            {badge && <Badge width="fit-content" {...badge} />}
            {badge2 && <Badge width="fit-content" {...badge2} />}
          </Flex>
          {isDone && (
            <DoneCircle>
              <Check
                size={16}
                color={semantic.text.color.inverse}
                weight="bold"
              />
            </DoneCircle>
          )}
        </Flex>
        <Title>{heading}</Title>
        {description && (
          <P variant="s" mt="xxs" color={semantic.text.color.secondary}>
            {description}
          </P>
        )}
        {children}
      </ActionHandler>
    </Card>
  );
};
